import React, { Fragment, useMemo } from 'react';

import { Box, TableCell, Typography } from '@mui/material';
import StatusIconCDS from '@sharedCR/coreDesignSystem/StatusIconCDS';
import { CheckboxCDS, RadioGroupCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import {
  overrideCellStyles,
  overrideMetaStyles,
} from 'app/shared/styles/checklistOverrideCell.styles';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {
  checklistItemDeterminationBaseOptions,
  ChecklistDisplaySettingType,
  ChecklistItemDeterminationType,
} from 'constants/checklists';
import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT } from 'constants/date';
import {
  formatChecklistValue,
  getChecklistResultStatus,
  isChecklistResultGlyphOnly,
  isChecklistResultStateNonPassing,
  isChecklistStateRowDetermination,
} from 'utils/checklist';

import { ChecklistsTestIds } from '../Checklists.testIds';

import { INDENT_PER_NESTING_LEVEL_PX } from './ChecklistItemsTable.constants';
import { resultStatusStyles, resultTextStyles } from './ChecklistItemsTable.styles';

const ChecklistItemsTableRow = ({
  checklistItem,
  isEditMode,
  isOverride,
  users,
  handleChange,
  itemLevel,
}) => {
  const nameCellStyles = useMemo(
    () => ({
      display: 'list-item',
      marginLeft: `${itemLevel * INDENT_PER_NESTING_LEVEL_PX}px`,
    }),
    [itemLevel],
  );

  const { name, calculations, expressionResult: initialDetermination, valueFormat } = checklistItem;
  const {
    pendingChangeStatus,
    result,
    pendingResult,
    wasOverriden,
    overridenBy,
    overrideDate,
    expressionResult: determination,
    state,
  } = calculations || {};

  const isCalculationsExist = !!calculations;
  const isOverridePendingChanged = !!pendingChangeStatus?.change;
  const {
    iconType: resultIconType,
    text: resultText,
    textColor: resultTextColor,
  } = getChecklistResultStatus({
    state,
    determination: initialDetermination,
    result: pendingResult || result,
    statusDisplayType: ChecklistDisplaySettingType.FINAL_RESULT,
  });
  const supervisorName = users[overridenBy] ? users[overridenBy].name : null;
  const formattedOverrideDate = overrideDate
    ? dayjs(overrideDate).format(COMMON_DATETIME_FORMAT)
    : null;

  const isDeterminationValueType = initialDetermination === ChecklistItemDeterminationType.VALUE;
  const isStateRow = isChecklistStateRowDetermination(initialDetermination);
  const isValueDisplayed = isDeterminationValueType && !isChecklistResultStateNonPassing(state);
  const isResultTextDisplayed =
    isStateRow || isDeterminationValueType || !isChecklistResultGlyphOnly(resultIconType);

  const formattedInitialDetermination = useMemo(
    () => formatChecklistValue({ value: initialDetermination, valueFormat }),
    [initialDetermination, valueFormat],
  );

  const formattedDetermination = useMemo(
    () => formatChecklistValue({ value: determination, valueFormat }),
    [determination, valueFormat],
  );

  const resultReason = isStateRow || isDeterminationValueType ? formattedDetermination : null;

  const isOverrideEditAllowed = !result || wasOverriden || isOverridePendingChanged;
  const isOverrideDisplayed =
    isCalculationsExist && isEditMode ? isOverrideEditAllowed : wasOverriden;

  return (
    <Fragment>
      <TableCell data-testid={ChecklistsTestIds.CHECKLIST_ITEMS_TABLE_ROW_NAME_CELL}>
        <Box component="span" sx={itemLevel ? nameCellStyles : undefined}>
          {name}
        </Box>
      </TableCell>

      <TableCell data-testid={ChecklistsTestIds.CHECKLIST_ITEMS_TABLE_ROW_EXPRESSION_RESULT_CELL}>
        {!isDeterminationValueType && (
          <RadioGroupCDS
            disabled
            dataTestId={ChecklistsTestIds.CHECKLIST_ITEMS_TABLE_ROW_DETERMINATION_RADIO}
            name="determination"
            options={checklistItemDeterminationBaseOptions}
            value={initialDetermination}
          />
        )}

        {isDeterminationValueType && formattedInitialDetermination}
      </TableCell>

      <TableCell data-testid={ChecklistsTestIds.CHECKLIST_ITEMS_TABLE_ROW_RESULT_CELL}>
        {isCalculationsExist && !isValueDisplayed && (
          <Box sx={isResultTextDisplayed ? resultStatusStyles : undefined}>
            <StatusIconCDS
              isOutlined
              dataTestId={ChecklistsTestIds.CHECKLIST_ITEMS_TABLE_ROW_RESULT_ICON}
              size={IconSize[32]}
              type={resultIconType}
            />

            {isResultTextDisplayed && (
              <Box sx={resultTextStyles}>
                <Typography
                  color={resultTextColor}
                  data-testid={ChecklistsTestIds.CHECKLIST_ITEMS_TABLE_ROW_RESULT_LABEL}
                  variant="medium14"
                >
                  {resultText}
                </Typography>

                {resultReason && (
                  <Typography
                    color="text.secondary"
                    data-testid={ChecklistsTestIds.CHECKLIST_ITEMS_TABLE_ROW_RESULT_REASON}
                    variant="regular12"
                  >
                    {resultReason}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {isValueDisplayed && formattedDetermination}
      </TableCell>

      <TableCell
        className="py-0"
        data-is-field-changed={isOverridePendingChanged}
        data-testid={ChecklistsTestIds.CHECKLIST_ITEMS_TABLE_ROW_WAS_OVERRIDEN_CELL}
      >
        <Box sx={overrideCellStyles}>
          {isOverrideDisplayed && !isDeterminationValueType && (
            <CheckboxCDS
              data-testid={ChecklistsTestIds.CHECKLIST_ITEMS_TABLE_ROW_OVERRIDEN_BY_CELL}
              disabled={!isEditMode}
              name={checklistItem.id}
              value={isOverride}
              onChange={handleChange}
            />
          )}

          {(supervisorName || formattedOverrideDate) && (
            <Box sx={overrideMetaStyles}>
              {supervisorName && <Typography variant="regular14">{supervisorName}</Typography>}

              {formattedOverrideDate && (
                <Typography color="text.secondary" variant="regular12">
                  {formattedOverrideDate}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </TableCell>
    </Fragment>
  );
};

ChecklistItemsTableRow.propTypes = {
  users: PropTypes.object.isRequired,
  isOverride: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  checklistItem: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  itemLevel: PropTypes.number.isRequired,
};

export default ChecklistItemsTableRow;
